import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, ClipboardList } from "lucide-react";
import { PageHeader } from "@/components/Common";
import { useAuth } from "@/contexts/AuthContext";
import { ApprovalsList } from "@/pages/Approvals";
import PolicyPreview from "@/components/PolicyPreview";

/**
 * Admin Requests: any staff member can create an administrative request
 * (leave, expense, resource, policy exception, etc). Requests are routed
 * into the Approvals workflow so approvers can Approve / Reject / mark
 * On Review / Escalate / re-open Pending with a description on each step.
 */
export default function AdminRequests() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [actionCtx, setActionCtx] = useState(null);
  const [approverSlots, setApproverSlots] = useState([{ id: "s1" }]);
  const [form, setForm] = useState({
    title: "", subject: "leave", description: "", branch: "", amount: 0,
    approvers: [""],
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [{ data: a }, { data: u }] = await Promise.all([
          api.get("/approvals?type=admin_request"),
          api.get("/users"),
        ]);
        if (!cancelled) { setItems(a); setUsers(u); }
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [refreshKey]);
  const load = () => setRefreshKey((k) => k + 1);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/approvals", {
        title: form.title,
        type: "admin_request",
        amount: parseFloat(form.amount) || 0,
        // Prepend the subject so approvers see it in-line
        description: `[${form.subject.toUpperCase()}] ${form.description || ""}`.trim(),
        branch: form.branch,
        approvers: form.approvers.filter(Boolean),
      });
      toast.success("Admin request submitted for approval");
      setOpen(false);
      setForm({ title: "", subject: "leave", description: "", branch: "", amount: 0, approvers: [""] });
      setApproverSlots([{ id: "s1" }]);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const performAction = async (action, comment) => {
    if (!actionCtx) return;
    try {
      await api.post(`/approvals/${actionCtx.approvalId}/action`, { action, comment });
      toast.success(`Marked as ${action.replaceAll("_", " ")}`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Administration"
        title="Admin Requests"
        subtitle="Submit administrative requests. Every request is routed through the Approvals workflow with statuses: Approved, Pending, On Review, Rejected or Escalated — each with a written justification."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-900 hover:bg-emerald-950 text-white"
                data-testid="admin-request-new-button">
                <Plus size={16} className="mr-1.5" /> New request
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-display flex items-center gap-2">
                  <ClipboardList size={16} /> New admin request
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-3" data-testid="admin-request-form">
                <div>
                  <Label>Title</Label>
                  <Input required value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="e.g. Time-off request for Ramadan"
                    className="mt-1" data-testid="admin-request-title-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Subject</Label>
                    <Select value={form.subject}
                      onValueChange={(v) => setForm({ ...form, subject: v })}>
                      <SelectTrigger className="mt-1" data-testid="admin-request-subject-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[
                          "leave", "expense_reimbursement", "resource_request",
                          "policy_exception", "training", "access_request",
                          "transfer", "other",
                        ].map((s) => (
                          <SelectItem key={s} value={s}>{s.replaceAll("_", " ")}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Amount (UGX, if any)</Label>
                    <Input type="number" value={form.amount}
                      onChange={(e) => setForm({ ...form, amount: e.target.value })}
                      className="mt-1" data-testid="admin-request-amount-input" />
                  </div>
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea rows={4} value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Explain the request in detail — dates, amounts, justification, etc."
                    className="mt-1"
                    data-testid="admin-request-description-input" />
                </div>
                <div>
                  <Label>Branch (optional)</Label>
                  <Input value={form.branch}
                    onChange={(e) => setForm({ ...form, branch: e.target.value })}
                    placeholder="e.g. Main Abrah" className="mt-1" />
                </div>
                <div className="space-y-2">
                  <Label>Approval chain (in order)</Label>
                  <PolicyPreview
                    type="admin_request"
                    amount={form.amount}
                    branch={form.branch}
                    onResolved={(d) => {
                      if (!d?.resolved) return;
                      const ids = d.approvers || [];
                      const hasAny = form.approvers.some(Boolean);
                      if (!hasAny && ids.length > 0) {
                        setApproverSlots(ids.map((_, i) => ({ id: `p${i}` })));
                        setForm((f) => ({ ...f, approvers: ids }));
                      }
                    }}
                  />
                  {form.approvers.map((aid, i) => (
                    <Select key={approverSlots[i]?.id || `slot-${i}`} value={aid}
                      onValueChange={(v) => {
                        const next = [...form.approvers];
                        next[i] = v;
                        setForm({ ...form, approvers: next });
                      }}>
                      <SelectTrigger data-testid={`admin-request-approver-${i}-select`}>
                        <SelectValue placeholder={`Approver ${i + 1}`} />
                      </SelectTrigger>
                      <SelectContent>
                        {users.filter((u) => u.id !== user?.id).map((u) => (
                          <SelectItem key={u.id} value={u.id}>
                            {u.name} · {u.role?.replaceAll("_", " ")}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ))}
                  <Button type="button" variant="ghost" size="sm"
                    onClick={() => {
                      setApproverSlots([...approverSlots, { id: `s${Date.now()}` }]);
                      setForm({ ...form, approvers: [...form.approvers, ""] });
                    }}>
                    + Add approver
                  </Button>
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={busy}
                    className="bg-emerald-900 hover:bg-emerald-950 text-white"
                    data-testid="admin-request-submit-button">
                    {busy ? "Submitting…" : "Submit for approval"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <ApprovalsList
        loading={loading}
        items={items}
        user={user}
        onAct={(approvalId, actionMeta) => setActionCtx({ approvalId, action: actionMeta })}
      />

      {/* Re-use ActionDialog from Approvals via inline mount */}
      <InlineActionDialog
        ctx={actionCtx}
        onClose={() => setActionCtx(null)}
        onSubmit={performAction}
      />
    </div>
  );
}

/** Small wrapper so we don't export ActionDialog separately. */
function InlineActionDialog({ ctx, onClose, onSubmit }) {
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (ctx) setComment(""); }, [ctx]);
  if (!ctx) return null;
  const action = ctx.action;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await onSubmit(action.key, comment); onClose(); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!ctx} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <action.Icon size={16} /> Mark as {action.label}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Description / Comment</Label>
            <Textarea rows={4} value={comment} onChange={(e) => setComment(e.target.value)}
              placeholder="Add a note for the requester and other approvers…"
              className="mt-1 resize-none"
              data-testid="admin-request-action-comment" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={busy} className={action.btnClass}
              data-testid={`admin-request-action-submit-${action.key}`}>
              {busy ? "Saving…" : `Confirm ${action.label}`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
